/**
 * Real Liberty Copper — Theme v2 JavaScript
 *
 * Cart helpers, event bus, and the slide-out cart drawer controller.
 * Alpine.js components are inline in their sections; coin-lookup.js is separate.
 */

/* ── Cart line update (full-cart page; kept for compatibility) ── */
window.updateLineItem = async function (key, qty) {
  try {
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    });
    const cart = await res.json();
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    return cart;
  } catch (err) {
    console.error('[RLC] Cart update failed:', err);
  }
};

/* ── Landing-page attribution ──────────────────────────────────────────────
 * Copy the first-touch landing cookies (set by landing-tracker.liquid) onto
 * cart.attributes so they ride checkout → order note_attributes. That's how a
 * paid order is attributed back to its landing page. No-op (returns early) for
 * any session that never landed on a tracked URL. Fire-and-forget; failures
 * never block add-to-cart or the drawer. */
function rlcReadCookie(name) {
  var m = document.cookie.match('(?:^|; )' + name.replace(/([.*+?^${}()|[\]\\])/g, '\\$1') + '=([^;]*)');
  return m ? decodeURIComponent(m[1]) : null;
}
window.rlcStampLanding = function () {
  try {
    var raw = rlcReadCookie('rlc_lp');
    if (!raw) return;                          // not a tracked session
    var d = JSON.parse(raw);
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({
        attributes: {
          landing_page: d.lp || '',
          landing_url: d.url || '',
          landing_at: d.ts || '',
          landing_vid: rlcReadCookie('rlc_vid') || '',
          utm_source: d.utm_source || '',
          utm_medium: d.utm_medium || '',
          utm_campaign: d.utm_campaign || '',
          utm_term: d.utm_term || '',
          utm_content: d.utm_content || ''
        }
      })
    }).catch(function () {});
  } catch (err) { /* non-blocking */ }
};

/* ── Cart count badge helper ── */
window.updateCartCount = function (count) {
  document.querySelectorAll('[data-cart-count]').forEach(function (el) {
    el.textContent = count;
    el.hidden = count === 0;
  });
};

document.addEventListener('cart:updated', function (e) {
  if (e.detail && typeof e.detail.item_count !== 'undefined') {
    window.updateCartCount(e.detail.item_count);
  }
});

/* ──────────────────────────────────────────────────────────────────────────
 * Cart drawer
 * ──────────────────────────────────────────────────────────────────────── */
const DRAWER_SECTION = 'cart-drawer';

/* Parse a rendered cart-drawer section string and apply it to the live DOM:
 * swap the refreshable content, sync the drawer + header counts. */
function rlcApplyDrawerHTML(html) {
  if (!html) return;
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const fresh = doc.querySelector('[data-cart-drawer-content]');
  const live = document.querySelector('[data-cart-drawer-content]');
  if (fresh && live) live.replaceWith(fresh);

  // Header count inside the drawer (outside the swapped content).
  const freshCount = doc.querySelector('[data-cart-drawer-count]');
  const count = freshCount ? parseInt(freshCount.textContent, 10) || 0 : null;
  if (count !== null) {
    document.querySelectorAll('[data-cart-drawer-count]').forEach(function (el) {
      el.textContent = count;
    });
    window.updateCartCount(count);
  }
}

/* Fetch the drawer section fresh (no cart mutation) via Section Rendering API. */
async function rlcFetchDrawer() {
  try {
    const res = await fetch('/?sections=' + DRAWER_SECTION, { headers: { accept: 'application/json' } });
    const data = await res.json();
    return data[DRAWER_SECTION];
  } catch (err) {
    console.error('[RLC] drawer fetch failed:', err);
    return null;
  }
}

function rlcSetDrawerOpenState(isOpen) {
  document.body.classList.toggle('rlc-cart-drawer-open', isOpen);
  document.querySelectorAll('[data-rlc-premium-sticky-atc]').forEach(function (stickyBar) {
    stickyBar.classList.toggle('rlc-cart-drawer-suppressed', isOpen);
  });
}

document.addEventListener('alpine:init', function () {
  window.Alpine.store('cartDrawer', {
    open: false,
    show() { this.open = true; rlcSetDrawerOpenState(true); },
    close() { this.open = false; rlcSetDrawerOpenState(false); },
    // Apply already-rendered section HTML (from an add/change response), then open.
    openWith(html) { rlcApplyDrawerHTML(html); this.open = true; rlcSetDrawerOpenState(true); },
    // Re-fetch fresh, then open (header-icon click).
    async openFresh() { const html = await rlcFetchDrawer(); rlcApplyDrawerHTML(html); this.open = true; rlcSetDrawerOpenState(true); },
    // Apply fresh HTML without changing open state (qty/remove while open).
    apply(html) { rlcApplyDrawerHTML(html); }
  });
});

/* Quantity / remove from inside the drawer.
 * - Always updates the Shopify cart line via /cart/change.js (with Section
 *   Rendering so the drawer re-renders server-side).
 * - For STANDARD (random) coins, also re-syncs the held-coin reservation count
 *   to the new quantity (token passed in). Serial coins never call with a token.
 */
window.rlcCartQty = async function (key, qty, token) {
  try {
    if (token) {
      // Keep the random reservation in sync with the cart quantity.
      const base = window.RLC_PROXY_BASE || '/apps/coin-lookup';
      await fetch(base + '/coin-reserve-random', {
        method: 'POST',
        headers: { 'content-type': 'application/json', accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ token: token, qty: qty })
      }).catch(function () {});
    }
    const res = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty, sections: DRAWER_SECTION, sections_url: location.pathname })
    });
    const cart = await res.json();
    if (cart.sections && cart.sections[DRAWER_SECTION] && window.Alpine) {
      window.Alpine.store('cartDrawer').apply(cart.sections[DRAWER_SECTION]);
    }
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    return cart;
  } catch (err) {
    console.error('[RLC] drawer qty update failed:', err);
  }
};

/* Alpine data factory for the per-line reservation hold countdown.
 * Counts down to reserved_at + TTL (default 30 min). Spread into x-data. */
window.rlcHoldTimer = function (reservedAtISO, ttlMinutes) {
  const ttl = (ttlMinutes || 30) * 60 * 1000;
  return {
    holdLabel: '',
    holdExpired: false,
    _holdInt: null,
    startHold() {
      const end = new Date(reservedAtISO).getTime() + ttl;
      const tick = () => {
        const ms = end - Date.now();
        if (ms <= 0) {
          this.holdExpired = true;
          this.holdLabel = '0:00';
          if (this._holdInt) clearInterval(this._holdInt);
          return;
        }
        const m = Math.floor(ms / 60000);
        const s = Math.floor((ms % 60000) / 1000);
        this.holdLabel = m + ':' + (s < 10 ? '0' : '') + s;
      };
      tick();
      this._holdInt = setInterval(tick, 1000);
    }
  };
};
